import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import Panel from '../Panel';
import CopySection, { FinishStepButton } from '../CopySection';
import styles from './ConnectionsPanel.module.scss';
import {
  IntegrationModals,
  renderDeviceIntegrationOption,
  useOnUnsupportedPlatform,
} from '@components/Integrations';
import { useDispatch, useSelector } from 'react-redux';
import analytics from '@support/analytics';
import {
  TrackingProps,
  promptToDisconnectConsolidatedIntegration,
  titleForDeviceIntegrationType,
  titleForIntegrationType,
  unsupportedInMAS,
} from '@components/Integrations/utils';
import { useAuthContext } from '@support/hooks/use-auth-context';
import { selectAllIntegrations, selectIntegrations } from '@reducers/user/integrations';
import { deleteIntegration } from '@actions/integrations';
import { AddIntegration, ConnectedIntegration } from '@components/Integrations/Integration';
import { RemoteIntegrationProvider, integrationIsOnDevice } from '@models/Integration';
import IntegrationIcons from '@components/Icons/IntegrationIcons';
import MultipleAccountsBtn from '@components/Integrations/MultipleAccountsBtn';
import { clearDeviceIntegrationSetupAborted } from '@actions/deviceIntegrations';
import { useLocation } from 'react-router-dom';

const trackConnectionScreen = (props: TrackingProps = {}) => {
  analytics.track('Onboarding in Progress', { screen: 'Connect Accounts', ...props });
  if (props.type) {
    analytics.trackConnectAccount(props.type);
  }
};

function Integrations() {
  const dispatch = useDispatch();
  const location = useLocation();
  const userIntegrations = useSelector(selectIntegrations);
  const [activeModal, setActiveModal] = useState(null);
  const onUnsupported = useOnUnsupportedPlatform({
    setActiveModal,
    forceDarkMode: location.pathname.includes('onboarding'),
    directUser: true,
  });

  const closeModal = () => setActiveModal(null);

  const sortedEnabledIntegrations = useMemo(() => {
    return userIntegrations.enabledIntegrations.sort((a, b) => {
      const aLabel = a.integration?.label || a.enablingProvider || '';
      const bLabel = b.integration?.label || b.enablingProvider || '';
      return aLabel.localeCompare(bLabel);
    });
  }, [userIntegrations.enabledIntegrations]);

  const [sortedConnectDeviceIntegrationOptions, moreIntegrationOptions] = useMemo(() => {
    const remoteIntegrations: RemoteIntegrationProvider[] = ['google', 'microsoft'];
    const connectedRemoteIntegrations = remoteIntegrations.filter((provider) =>
      userIntegrations.hasIntegration(provider),
    );
    const remoteIntegrationOptions = remoteIntegrations.filter(
      (provider) => !userIntegrations.hasIntegration(provider),
    );

    return [
      userIntegrations.connectOnDeviceOptions.concat(remoteIntegrationOptions).sort((a, b) => {
        const aLabel = titleForDeviceIntegrationType[a] || a || '';
        const bLabel = titleForDeviceIntegrationType[b] || b || '';
        return aLabel.localeCompare(bLabel);
      }),
      connectedRemoteIntegrations.map((provider) => ({
        title: titleForIntegrationType[provider] || provider,
        action: () => setActiveModal(provider),
        icon: IntegrationIcons[provider],
      })),
    ];
  }, [userIntegrations]);

  useEffect(() => {
    if (userIntegrations.deviceIntegrationSetupAborted) {
      closeModal();
      dispatch(clearDeviceIntegrationSetupAborted());
    }
  }, [userIntegrations.deviceIntegrationSetupAborted]);

  return (
    <>
      <div className={styles.optionsWrapper}>
        {sortedEnabledIntegrations.map(({ integration, enablingProvider }) => {
          if (integration?.isOnDevice || enablingProvider) {
            return renderDeviceIntegrationOption({
              integrationProvider: integration?.provider || enablingProvider,
              className: styles.integrationOption,
              isInOnboarding: true,
              setActiveModal,
              onUnsupported,
            });
          }

          return (
            <ConnectedIntegration
              key={`${integration.type}_${integration.displayName}`}
              className={styles.integrationOption}
              integration={integration}
              isInOnboarding
              removeIntegration={() => {
                promptToDisconnectConsolidatedIntegration({
                  integration,
                  deleteIntegration: () => dispatch(deleteIntegration(integration)),
                });
              }}
            />
          );
        })}

        {sortedConnectDeviceIntegrationOptions.map((provider) => {
          if (window.desktopIntegrations?.appInfo?.isMAS && unsupportedInMAS(provider)) {
            return null;
          }

          if (integrationIsOnDevice(provider)) {
            return renderDeviceIntegrationOption({
              integrationProvider: provider,
              className: styles.integrationOption,
              isInOnboarding: true,
              setActiveModal,
              onUnsupported,
            });
          }

          return (
            <AddIntegration
              key={provider}
              className={styles.integrationOption}
              provider={provider}
              label={titleForIntegrationType[provider] || provider}
              onClick={() => setActiveModal(provider)}
              isInOnboarding
            />
          );
        })}
      </div>
      <MultipleAccountsBtn options={moreIntegrationOptions} />
      <IntegrationModals
        activeModal={activeModal}
        closeModal={closeModal}
        forceDarkMode
        trackConnection={trackConnectionScreen}
        renderLocation="onboarding"
      />
    </>
  );
}

export default function IntegrationsPanel({
  proceedToNextStep,
  setShowImportingContactsAlert,
}: {
  proceedToNextStep: () => void;
  setShowImportingContactsAlert: Dispatch<boolean>;
}) {
  const user = useAuthContext();
  // const { accountProcessingStatus } = useAuthContext();

  const integrations = useSelector(selectAllIntegrations);

  useEffect(() => {
    trackConnectionScreen();
  }, []);

  // show if more than 0 integrations AND user account is not "complete" (or whatever the signal is)
  // BUT don't use polling, just show it for the rest of the onboarding
  useEffect(() => {
    if (integrations.length > 0 && user.accountProcessingStatus !== 'complete') {
      setShowImportingContactsAlert(true);
    }
  }, [integrations.length]);

  return (
    <>
      <Panel>
        <CopySection>
          <h2>Connect Accounts</h2>
          <p>Connect at least one account to get started.</p>
          <p>
            <a href="https://clay.earth/security-and-privacy" target="_blank">
              Learn more
            </a>{' '}
            about how we protect your privacy.
          </p>
          <FinishStepButton
            onClick={proceedToNextStep}
            disabled={integrations.length == 0 && !user.getEntitlementValue('account.team_member')}
            disabledMsg="Connect at least one account to continue"
          >
            Continue
          </FinishStepButton>
        </CopySection>
        <div className={styles.accountOptionsContainer}>
          <Integrations />
        </div>
      </Panel>
    </>
  );
}
